import { ArrowBendUpRightIcon, FolderMinusIcon } from '@phosphor-icons/react'
import classNames from 'classnames'
import { Link } from 'react-router'
import { Routes } from '../../routes.ts'
import type { Repository } from '../../utils/api-client/api-schemas.ts'
import { Checkbox } from '../input/checkbox.tsx'
import { SCMIcon } from '../third-party-branding/scm-icon.tsx'
import { TooltipWithHover } from '../tooltip.tsx'
import * as styles from './project-row-card.css.ts'

export type ProjectRepositoryCardProps = {
  repository: Pick<Repository, 'id' | 'full_name' | 'type'>
  /** The row's own detail — a risk score, when the repository has one. */
  riskScore?: number
  /** When the repository was last scanned, already phrased ("2 years ago"). */
  lastScanned?: string
  /** Offered to administrators only; a reader gets the row without its actions. */
  onMove?: () => unknown
  /** Why the move action is shown but can't be used. When set, the button stays visible and does nothing. */
  moveDisabledReason?: string
  onRemove?: () => unknown
  /**
   * The row's place in a multi-select, in the shape the findings table uses: a checkbox of its own and
   * a wash when checked. Absent for a reader, who gets no checkbox column at all.
   */
  selection?: { isSelected: boolean; onToggle: () => unknown }
}

/**
 * One repository in a project: a row card whose name opens the repository, with move and remove
 * revealed on hover and on keyboard focus.
 *
 * A click anywhere else on the card toggles its selection. That is a pointer shortcut only: the
 * checkbox stays the keyboard and screen-reader control, so the card itself is not a control and
 * the link, checkbox and actions are never nested inside one.
 */
export const ProjectRepositoryCard: React.FC<ProjectRepositoryCardProps> = ({
  repository,
  riskScore,
  lastScanned,
  onMove,
  moveDisabledReason,
  onRemove,
  selection,
}) => {
  const handleCardClick = (event: React.MouseEvent<HTMLElement>) => {
    if (selection && isOwnClick(event)) {
      selection.onToggle()
    }
  }

  return (
    <div
      className={classNames(selection ? styles.cardSelectable : [styles.card, styles.cardInert], {
        [styles.cardSelected]: selection?.isSelected,
      })}
      onClick={selection ? handleCardClick : undefined}
    >
      {selection && (
        <span className={styles.checkboxCell}>
          <Checkbox
            checked={selection.isSelected}
            onCheckedChange={() => selection.onToggle()}
            ariaLabel={`Select ${repository.full_name}`}
          />
        </span>
      )}
      <span className={styles.badge}>
        <SCMIcon type={repository.type} size="medium" />
      </span>
      <span className={styles.meta}>
        <p className={styles.name}>
          <Link
            className={styles.nameLink}
            to={Routes.RepositoryDetailsPage.createPath({ repositoryId: repository.id })}
            aria-label={`Open ${repository.full_name}`}
          >
            {repository.full_name}
          </Link>
        </p>
        <p className={styles.sub}>
          <span>Risk {riskScore ?? '—'}</span>
          <i className={styles.subSeparator}>·</i>
          <span>Last scan {lastScanned ?? 'never'}</span>
        </p>
      </span>
      {(onMove || onRemove) && (
        <span className={styles.actions}>
          {onMove && (
            <TooltipWithHover
              size="small"
              trigger={
                <button
                  type="button"
                  className={styles.actionButton}
                  aria-label={`Move ${repository.full_name} to another project`}
                  // `aria-disabled` rather than `disabled`: a disabled button gets no pointer events, so its
                  // tooltip — the only place the reason is given — would never open.
                  aria-disabled={moveDisabledReason ? true : undefined}
                  onClick={moveDisabledReason ? undefined : () => onMove()}
                >
                  <ArrowBendUpRightIcon size={18} weight="light" aria-hidden />
                </button>
              }
            >
              {moveDisabledReason ?? 'Move to another project'}
            </TooltipWithHover>
          )}
          {onRemove && (
            <TooltipWithHover
              size="small"
              trigger={
                <button
                  type="button"
                  className={styles.actionButton}
                  aria-label={`Remove ${repository.full_name} from this project`}
                  onClick={() => onRemove()}
                >
                  <FolderMinusIcon size={18} weight="light" aria-hidden />
                </button>
              }
            >
              Remove from this project
            </TooltipWithHover>
          )}
        </span>
      )}
    </div>
  )
}

/**
 * Whether a click on the card was meant for the card. The link, the checkbox and the row actions
 * handle their own clicks, and a drag that selected text is a reader copying a name, not choosing a row.
 */
const isOwnClick = (event: React.MouseEvent<HTMLElement>) =>
  !(event.target instanceof Element && event.target.closest('a, button, [role="checkbox"]')) &&
  !window.getSelection()?.toString()
